import { HttpErrorResponse, HttpContext } from '@angular/common/http';
import { Injectable, Signal, WritableSignal, inject, signal } from '@angular/core';
import { AuthService } from '@app/api/auth';
import type { CurrentUserResponse } from '@app/api/auth';
import { SUPPRESS_UNAUTHORIZED_REDIRECT } from '@app/core/interceptor/http-context-tokens';
import { catchError, firstValueFrom, of, throwError } from 'rxjs';

export interface CurrentUser {
  userId: string;
  displayName: string;
  name: string;
  username: string;
  roles: string[];
  forceGlobalReader: boolean;
  forceGlobalReaderMessage?: string | null;
}

/**
 * Service responsible for managing user authentication state and information.
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly authService = inject(AuthService);

  /**
   * Private writable signal holding the current user state.
   * @private
   */
  private readonly _user: WritableSignal<CurrentUser | null> = signal(null);
  private loadingUser: Promise<CurrentUser | null> | null = null;

  /**
   * Public read-only signal exposing the current user state.
   */
  public readonly user: Signal<CurrentUser | null> = this._user.asReadonly();

  public async fetchUser(forceRefresh = false): Promise<CurrentUser | null> {
    if (!forceRefresh && this.user()) {
      return this.user();
    }

    if (!forceRefresh && this.loadingUser) {
      return this.loadingUser;
    }

    this.loadingUser = firstValueFrom(
      this.authService.meGet('body', false, {
        context: new HttpContext().set(SUPPRESS_UNAUTHORIZED_REDIRECT, true),
      }).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 401) {
            return of(null);
          }

          return throwError(() => error);
        }),
      ),
    )
      .then(user => {
        const currentUser = user ? this.mapAuthenticatedUser(user) : null;
        this._user.set(currentUser);
        return currentUser;
      })
      .finally(() => {
        this.loadingUser = null;
      });

    return this.loadingUser;
  }

  public isLoggedIn(): boolean {
    return this.user() !== null;
  }

  public login(returnUrl?: string): void {
    const target = returnUrl ?? `${location.pathname}${location.search}${location.hash}`;
    location.assign(this.getAuthNavigationUrl('login', target));
  }

  public logout(returnUrl = '/'): void {
    this._user.set(null);
    location.assign(this.getAuthNavigationUrl('logout', returnUrl));
  }

  private mapAuthenticatedUser(user: CurrentUserResponse): CurrentUser {
    return {
      userId: user.userId,
      displayName: user.displayName,
      name: user.displayName,
      username: user.userId,
      roles: user.roles,
      forceGlobalReader: user.forceGlobalReader,
      forceGlobalReaderMessage: user.forceGlobalReaderMessage,
    };
  }

  private getAuthNavigationUrl(action: 'login' | 'logout', returnUrl: string): string {
    const authBasePath = this.authService.configuration.basePath ?? '/api/auth';
    const normalizedBasePath = authBasePath.endsWith('/')
      ? authBasePath.slice(0, -1)
      : authBasePath;

    return `${normalizedBasePath}/${action}?returnUrl=${encodeURIComponent(returnUrl)}`;
  }
}
